<script setup lang="ts">
/**
 * Dropdown for the drawing tool's dark panels.
 *
 * A native <select> hands its option list to the operating system, which draws it in
 * the system theme — on Windows and on some tablets that means white text on a white
 * popup, unreadable. `color-scheme: dark` styles the closed control but never reaches
 * that popup, so the list is drawn here instead.
 *
 * The list is teleported to the body: inside the panel it would be clipped by the
 * scrolling content.
 */
import { ref, computed, nextTick, onBeforeUnmount } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_MODAL } from "./layouts/constants";
import { clampBoxToCanvas, getCanvasBounds } from "./canvasBounds";

const props = defineProps<{
  modelValue: string;
  options: string[];
  /** Shown when the current value is unknown — ODA cannot always read one back. */
  placeholder?: string;
}>();

const emit = defineEmits<{ "update:modelValue": [value: string] }>();

/** Row height and padding, used to guess the list size before it is rendered. */
const ITEM_HEIGHT = 30;
const LIST_PADDING = 8;
const MAX_LIST_HEIGHT = 260;

const trigger = ref<HTMLElement | null>(null);
const list = ref<HTMLElement | null>(null);
const listStyle = ref<Record<string, string> | null>(null);

const isOpen = computed(() => listStyle.value !== null);
const label = computed(() => props.modelValue || props.placeholder || "");

function listHeight(): number {
  return Math.min(
    props.options.length * ITEM_HEIGHT + LIST_PADDING,
    MAX_LIST_HEIGHT,
  );
}

function open(): void {
  const rect = trigger.value?.getBoundingClientRect();
  if (!rect) return;

  const bounds = getCanvasBounds();
  const height = listHeight();
  // Below the control, unless there is no room left down there.
  const fitsBelow = rect.bottom + height <= bounds.top + bounds.height;
  const top = fitsBelow ? rect.bottom + 2 : rect.top - height - 2;
  const width = Math.max(rect.width, 120);

  const placed = clampBoxToCanvas(
    rect.right - width,
    top,
    width,
    height,
    bounds,
  );
  listStyle.value = {
    left: `${placed.left}px`,
    top: `${placed.top}px`,
    width: `${width}px`,
    maxHeight: `${MAX_LIST_HEIGHT}px`,
    zIndex: String(Z_INDEX_MODAL + 1),
  };
  window.addEventListener("keydown", onKeydown);

  // Long lists (a dozen scales) open scrolled to the top otherwise, hiding the
  // value that is actually set.
  nextTick(() => {
    list.value
      ?.querySelector(".canvas-select-option.active")
      ?.scrollIntoView({ block: "nearest" });
  });
}

function close(): void {
  listStyle.value = null;
  window.removeEventListener("keydown", onKeydown);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") close();
}

function pick(value: string): void {
  close();
  if (value !== props.modelValue) emit("update:modelValue", value);
}

onBeforeUnmount(close);
</script>

<template>
  <button
    ref="trigger"
    type="button"
    class="canvas-select"
    :class="{ open: isOpen, placeholder: !modelValue }"
    role="combobox"
    :aria-expanded="isOpen"
    @click="isOpen ? close() : open()"
  >
    <span class="canvas-select-label">{{ label }}</span>
    <PhosphorIcon name="caret-down" :size="12" />
  </button>

  <Teleport to="body">
    <div
      v-if="listStyle"
      class="canvas-select-backdrop"
      :style="{ zIndex: Z_INDEX_MODAL }"
      @pointerdown.self="close"
    >
      <div
        ref="list"
        class="canvas-select-list"
        :style="listStyle"
        role="listbox"
      >
        <button
          v-for="option in options"
          :key="option"
          type="button"
          role="option"
          :aria-selected="option === modelValue"
          class="canvas-select-option"
          :class="{ active: option === modelValue }"
          @click="() => pick(option)"
        >
          {{ option }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.canvas-select {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 6px;
  border-radius: 4px;
  border: 1px solid var(--canvas-border);
  background-color: rgba(255, 255, 255, 0.06);
  color: white;
  font-size: 0.8rem;

  &:hover,
  &.open {
    background-color: rgba(255, 255, 255, 0.12);
  }

  &.placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
}

.canvas-select-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.canvas-select-backdrop {
  position: fixed;
  inset: 0;
}

.canvas-select-list {
  position: fixed;
  overflow-y: auto;
  padding: 4px 0;
  border-radius: 6px;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
  // Dark scrollbar on every platform, like the panels.
  color-scheme: dark;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.canvas-select-option {
  display: block;
  width: 100%;
  padding: 6px 12px;
  border: 0;
  background: transparent;
  color: white;
  text-align: left;
  font-size: 0.8rem;

  &:hover,
  &:focus {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: var(--canvas-btn-active-bg);
    color: #1a1a1a;
  }
}
</style>
